import React, {useState} from "react";
import {Link} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {toVND} from "../../../helpers/helpers";
import {removeItem, clearCart, updateItem} from "../../../slices/Web/cart/cartSlice";
import {DEFAULT_PRODUCT_IMAGE} from "../../../constants/images";
import DetailMenuOffcanvas from "../DetailMenuOffcanvas";
import ConfirmModal from "../ConfirmModal";

interface CartProps {
    onCheckout?: () => void;
    onBack?: () => void;
}

const Cart: React.FC<CartProps> = ({onCheckout, onBack}) => {
    const dispatch = useDispatch();
    const cart = useSelector((state: any) => state.Cart);
    const {shopMenu} = useSelector((state: any) => state.ShopMenu);
    const shopInfo = shopMenu?.shop ?? shopMenu;
    const hasImage = shopInfo?.hasImage;
    const totalQuantity = cart.items.reduce(
        (sum: number, item: any) => sum + (item.quantity || 0),
        0
    );

    // Tính toán các khoản phí và giảm giá dựa trên dữ liệu giỏ hàng
    const subtotal = cart.total;
    // TODO: Sẽ dùng lại trong tương lai.
    // const taxRate = 0.1; // Thuế 10%
    // const tax = subtotal * taxRate;
    // Giảm 5% nếu số lượng món từ 3 trở lên
    // const discount = totalQuantity >= 3 ? subtotal * 0.05 : 0;
    const grandTotal = subtotal;

    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [removeId, setRemoveId] = useState<string | null>(null);
    const [showClearModal, setShowClearModal] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingItem, setEditingItem] = useState<any>(null);
    const [showEditMenu, setShowEditMenu] = useState(false);
    const [showCheckoutModal, setShowCheckoutModal] = useState(false);

    const handleRemove = (id: string) => {
        setRemoveId(id);
        setShowRemoveModal(true);
    };

    const confirmRemove = () => {
        if (removeId !== null) {
            dispatch(removeItem(removeId));
        }
        setShowRemoveModal(false);
        setRemoveId(null);
    };

    const cancelRemove = () => {
        setShowRemoveModal(false);
        setRemoveId(null);
    };

    const handleClear = () => {
        setShowClearModal(true);
    };

    const confirmClear = () => {
        dispatch(clearCart());
        setShowClearModal(false);
    };

    const cancelClear = () => {
        setShowClearModal(false);
    };

    const handleEdit = (item: any, index: number) => {
        setEditingIndex(index);
        setEditingItem(item);
        setShowEditMenu(true);
    };

    const handleCloseEdit = () => {
        setShowEditMenu(false);
        setEditingIndex(null);
        setEditingItem(null);
    };

    const handleSaveEdit = (updated: any) => {
        if (editingIndex !== null && editingItem) {
            dispatch(
                updateItem({
                    index: editingIndex,
                    item: {...updated, cartItemId: editingItem.cartItemId},
                })
            );
        }
        handleCloseEdit();
    };

    const handleCheckoutRequest = () => {
        setShowCheckoutModal(true);
    };

    const confirmCheckout = () => {
        setShowCheckoutModal(false);
        if (onCheckout) {
            onCheckout();
        }
    };

    const cancelCheckout = () => {
        setShowCheckoutModal(false);
    };

    return (
        <React.Fragment>
            <ConfirmModal
                isOpen={showRemoveModal}
                typeConfirm="danger"
                title="Xác nhận xóa"
                content="Bạn có chắc chắn muốn xóa món này không?"
                onConfirm={confirmRemove}
                onCancel={cancelRemove}
            />
            <ConfirmModal
                isOpen={showClearModal}
                typeConfirm="danger"
                title="Xác nhận xóa"
                content="Bạn có chắc chắn muốn xóa tất cả món trong giỏ hàng không?"
                onConfirm={confirmClear}
                onCancel={cancelClear}
            />
            <ConfirmModal
                isOpen={showCheckoutModal}
                typeConfirm="primary"
                title="Xác nhận đặt món"
                content="Xác nhận không thay đổi đơn hàng?"
                onConfirm={confirmCheckout}
                onCancel={cancelCheckout}
            />
            {showEditMenu && editingItem && (
                <DetailMenuOffcanvas
                    menuId={String(editingItem.id)}
                    isOpen={showEditMenu}
                    onClose={handleCloseEdit}
                    initialSelectedOptions={editingItem.option}
                    initialNote={editingItem.note}
                    initialQuantity={editingItem.quantity}
                    onSave={handleSaveEdit}
                />
            )}
            <div className="header-area" id="headerArea">
                <div
                    className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <div className="back-button me-2">
                        <button
                            className="btn btn-link p-0 border-0"
                            type="button"
                            onClick={onBack}
                        >
                            <i className="mdi mdi-arrow-left"></i>
                        </button>
                    </div>

                    <div className="page-heading">
                        <h6 className="mb-0">Đơn đặt hàng của bạn</h6>
                    </div>

                    <div className="suha-navbar-toggler border-unset ms-2"></div>
                </div>
            </div>
            <div className="page-content-wrapper">
                <div className="container">
                    <div className="cart-wrapper-area py-3">
                        {cart.items.length === 0 ? (
                            <div className="text-center py-5">
                                <p className="mb-3">Giỏ hàng trống</p>
                                <button
                                    className="btn btn-primary"
                                    type="button"
                                    onClick={onBack}
                                >
                                    Quay lại menu
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="cart-table card mb-3">
                                    <div className="table-responsive card-body">
                                        <table className="table mb-0">
                                            <tbody>
                                            {cart.items.map((item: any, i: number) => (
                                                <tr key={i}>
                                                    {hasImage && (
                                                        <td>
                                                            <img
                                                                className="rounded"
                                                                src={item.imageUrl || DEFAULT_PRODUCT_IMAGE}
                                                                alt=""
                                                            />
                                                        </td>
                                                    )}
                                                    <td>
                                                        <Link
                                                            className="product-title"
                                                            to="#"
                                                        >
                                                            {item.productName}
                                                            <span className="mt-1">
                                                                {toVND(item.price)} x {item.quantity}
                                                            </span>
                                                        </Link>
                                                        {item.option && (
                                                            <ul className="mb-1 ps-0">
                                                                {Object.values(item.option).map(
                                                                    (opt: any, idx: number) =>
                                                                        Array.isArray(opt)
                                                                            ? opt.map(
                                                                                (
                                                                                    o: any,
                                                                                    j: number
                                                                                ) => (
                                                                                    <li
                                                                                        key={`${idx}-${j}`}
                                                                                        className="small text-muted"
                                                                                    >
                                                                                        {o.name}
                                                                                    </li>
                                                                                )
                                                                            )
                                                                            : (
                                                                                <li
                                                                                    key={idx}
                                                                                    className="small text-muted"
                                                                                >
                                                                                    {opt.name}
                                                                                </li>
                                                                            )
                                                                )}
                                                            </ul>
                                                        )}
                                                        {item.note && (
                                                            <small className="text-muted">
                                                                {item.note}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td className="align-middle text-end">
                                                        <div className="d-flex flex-column align-items-end gap-2">
                                                            <button
                                                                className="btn btn-secondary btn-sm text-white"
                                                                onClick={() => handleEdit(item, i)}
                                                            >
                                                                <i className="mdi mdi-pencil me-1"></i>
                                                                Sửa
                                                            </button>

                                                            <button
                                                                className="btn btn-danger btn-sm text-white"
                                                                onClick={() => handleRemove(item.cartItemId)}
                                                            >
                                                                <i className="mdi mdi-delete me-1"></i>
                                                                Xóa
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="card cart-amount-area">
                                    <div className="card-body">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <span>Tạm tính</span>
                                            <span>{toVND(subtotal)}</span>
                                        </div>
                                        {/*
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <span>Thuế (10%)</span>
                                            <span>{toVND(tax)}</span>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <span>Giảm giá</span>
                                            <span>{discount > 0 ? `-${toVND(discount)}` : toVND(0)}</span>
                                        </div>
                                        */}
                                        <div className="d-flex align-items-center justify-content-between">
                                            <h5 className="mb-0">Tổng cộng</h5>
                                            <h5 className="total-price mb-0">
                                                {toVND(grandTotal)} ({totalQuantity} món)
                                            </h5>
                                        </div>
                                        <div className="mt-3 d-flex align-items-center justify-content-end">
                                            <button className="btn btn-primary me-2" onClick={handleCheckoutRequest}>
                                                Đặt món
                                            </button>
                                            <button className="btn btn-outline-danger" onClick={handleClear}>
                                                Xóa tất cả
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default Cart;
